import { string, bool, func } from 'prop-types';
import { withProps, withStateHandlers, compose } from 'recompose';
import { connect } from 'react-redux';

import themeColors from 'styles/themes/colors';

import generateRandomHash from 'helpers/generateRandomHash';
import { entityTypeToApiType } from 'helpers/mapEntityTypes';

import { ArtworkWrapper, ArtworkMask } from './Artwork.style.js';

import Placeholder from './Placeholder/Placeholder';
import ActiveBorder from './ActiveBorder/ActiveBorder';
import SVGArtworkMask from './SVGArtworkMask/SVGArtworkMask';
import ArtworkImage from './ArtworkImage/ArtworkImage';

/**
 * Displays the Entity artwork. It shows a placeholder when no source is provided and when the image is not yet loaded.
 */
export const Artwork = ({
  title,
  src,
  entityType,
  isActive,
  isVersion,
  onError,
  hasError,
  className,
  forBreadcrumb,
}) => {
  const hash = generateRandomHash();
  const maskId = `artwork_mask_${hash}`;
  const activeBorderId = `active_border_${hash}`;
  const placeholderGradientId = `placeholder_gradient_${hash}`;

  /**
   * Make sure to update the groupArtworkRatio variable in variables.js when updating these values!
   * */
  const strokeWidth = 3;
  const baseWidth = forBreadcrumb ? 30 : 100;
  const baseHeight = forBreadcrumb ? 30 : 100;
  const svgWidth = baseWidth + strokeWidth * 2;
  const svgHeight = baseHeight + strokeWidth * 2;

  const showImage = src && !hasError;

  return (
    <ArtworkWrapper className={className} data-pw="artwork-wrapper">
      <ArtworkMask entityType={entityType}>
        <svg
          width={svgWidth}
          height={svgHeight}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          preserveAspectRatio="xMidYMid slice"
        >
          {title && <title>Artwork for {title}</title>}
          <defs>
            <linearGradient
              /** This is the gradient of the ActiveBorder */

              x1="50%"
              y1="0"
              x2="50%"
              y2="100%"
              id={activeBorderId}
            >
              <stop stopColor={themeColors.GradientBlue} offset="0%" />
              <stop stopColor={themeColors.GradientGreen} offset="100%" />
            </linearGradient>
            {!showImage && entityType !== 'project' && (
              <linearGradient
                /** This is the Placeholder gradient for non-project entityTypes */
                x1="0%"
                y1="50%"
                x2="100%"
                y2="50%"
                id={placeholderGradientId}
              >
                <stop
                  stopColor={themeColors.NightBlue}
                  stopOpacity="0.1"
                  offset="0%"
                />
                <stop
                  stopColor={themeColors.NightBlue}
                  stopOpacity="0.15"
                  offset="100%"
                />
              </linearGradient>
            )}
            {!showImage && entityType === 'project' && (
              <>
                <linearGradient
                  /** These are the Placeholder gradients for project entityTypes */
                  x1="0%"
                  y1="50%"
                  x2="100%"
                  y2="50%"
                  id={`${placeholderGradientId}_1`}
                >
                  <stop
                    stopColor={themeColors.NightBlue}
                    stopOpacity="0.1"
                    offset="0%"
                  />
                  <stop
                    stopColor={themeColors.NightBlue}
                    stopOpacity="0.125"
                    offset="100%"
                  />
                </linearGradient>
                <linearGradient
                  /** This is the Placeholder gradient for non-project entityTypes */
                  x1="0%"
                  y1="50%"
                  x2="100%"
                  y2="50%"
                  id={`${placeholderGradientId}_2`}
                >
                  <stop
                    stopColor={themeColors.NightBlue}
                    stopOpacity="0.125"
                    offset="0%"
                  />
                  <stop
                    stopColor={themeColors.NightBlue}
                    stopOpacity="0.15"
                    offset="100%"
                  />
                </linearGradient>
              </>
            )}
            {showImage && (
              <clipPath id={maskId}>
                <SVGArtworkMask
                  entityType={entityType}
                  isVersion={isVersion}
                  forBreadcrumb={forBreadcrumb}
                />
              </clipPath>
            )}
          </defs>
          <g transform={`translate(${strokeWidth},${strokeWidth})`}>
            {!showImage && (
              <Placeholder
                /** Display a Placeholder; this is shown when there is no image or the image is not yet loaded */
                entityType={entityType}
                isVersion={isVersion}
                placeholderGradientId={placeholderGradientId}
                forBreadcrumb={forBreadcrumb}
              />
            )}
            {showImage && (
              <ArtworkImage
                src={src}
                maskId={maskId}
                onError={onError}
                entityType={entityType}
                isVersion={isVersion}
                forBreadcrumb={forBreadcrumb}
              />
            )}
            {!forBreadcrumb && (
              <ActiveBorder
                transform={`rotate(-90,${baseWidth / 2},${baseWidth / 2})`}
                activeBorderId={activeBorderId}
                entityType={entityType}
                isVersion={isVersion}
                strokeWidth={strokeWidth}
                stroke={`url(#${activeBorderId})`}
                fill="none"
                fillRule="evenodd"
                isActive={isActive}
                showBorder={isActive}
              />
            )}
          </g>
        </svg>
      </ArtworkMask>
    </ArtworkWrapper>
  );
};

Artwork.defaultProps = {
  src: null,
};

Artwork.propTypes = {
  /** Optional class name */
  className: string,
  /** Artwork image URL, Max image size = 80x80px */
  src: string,
  /** Artwork title for ALT attribute */
  title: string,
  /** Displays a different Placeholder per entityType */
  entityType: string.isRequired,
  /** Should the artwork have a thicker line indicating active style? */
  isActive: bool,
  /** Do we want to show a double artwork */
  isVersion: bool,
  /** Method that is called when the loading of the image fails */
  onError: func,
  /** Is the component currently in error state? */
  hasError: bool,
  forBreadcrumb: bool,
};

export default compose(
  connect((state) => ({
    followingEntities: state.userSettings.data.followingEntities,
  })),
  withProps(({ followingEntities, entityType, entityId }) => {
    if (!entityId) {
      return {
        isActive: false,
      };
    }

    const isActive =
      followingEntities &&
      followingEntities.filter(
        (item) =>
          item.id === entityId && item.type === entityTypeToApiType(entityType)
      ).length > 0;

    return {
      isActive,
    };
  }),
  withStateHandlers(
    { hasError: false },
    {
      onError: () => () => ({
        hasError: true,
      }),
    }
  )
)(Artwork);
